import { AttributDataType, Erfuellungsgrad } from '@comparit/common-api';
import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import type { VergleichAttributInput } from '../../interfaces/vergleich-input.model';
import {
  SpecialNumericLabelPipe,
  getSpecialNumericLabel,
} from '../../pipes/special-numeric-label.pipe';

/**
 * How a unit is written next to a value, ported from B2B's comparer. `einheit` carries the
 * backend enum's *name*, so showing it raw would put „Euro“ next to the number instead of „€“.
 * A name this table does not know shows no unit rather than leaking the raw enum member.
 */
const EINHEIT_LABELS: Readonly<Record<string, string>> = {
  OhneEinheit: '',
  Prozent: '%',
  Euro: '€',
  TausendEuro: 'Tsd. €',
  MillionenEuro: 'Mio. €',
  Quadratmeter: 'm²',
  Monate: 'Monate',
  Jahre: 'Jahre',
  Tage: 'Tage',
};

/** One cell of the comparison table: one tariff's answer for one attribute. */
@Component({
  selector: 'ui-vergleich-box',
  templateUrl: './vergleich-box.component.html',
  styleUrl: './vergleich-box.component.scss',
  imports: [MatIcon, SpecialNumericLabelPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VergleichBoxComponent {
  readonly item = input.required<VergleichAttributInput>();
  readonly showBedingungstexte = input(false);

  protected readonly expanded = signal(false);

  protected readonly isBoolean = computed(() => this.item().dataType === AttributDataType.Boolean);
  protected readonly isChecked = computed(() => this.item().attributaussage === true);
  protected readonly isFilled = computed(
    () => this.item().erfuellungsgrad === Erfuellungsgrad.Erfuellt
  );

  // The flat shape carries every member's value, so `dataType` plus `typeof` narrows it without a cast.
  protected readonly numberValue = computed(() => {
    const { dataType, attributaussage } = this.item();
    return dataType === AttributDataType.Number && typeof attributaussage === 'number'
      ? attributaussage
      : null;
  });

  protected readonly stringValue = computed(() => {
    const { dataType, attributaussage } = this.item();
    return dataType === AttributDataType.String && typeof attributaussage === 'string'
      ? attributaussage
      : null;
  });

  /** The List member keeps its values under the capitalised sibling of `attributaussage`. */
  protected readonly listValues = computed(() => {
    const { dataType, attributAussage } = this.item();
    return dataType === AttributDataType.List && attributAussage?.length ? attributAussage : null;
  });

  protected readonly einheitSuffix = computed(() => {
    const value = this.numberValue();
    // A sentinel already reads as prose ("unbegrenzt"); a unit behind it would be nonsense.
    if (value === null || getSpecialNumericLabel(value)) return '';

    const unit = EINHEIT_LABELS[this.item().einheit ?? ''] ?? '';
    return unit ? ` ${unit}` : '';
  });
}
